import { createStore } from "vuex";
import _ from "lodash";

const emptyPersistedState = {
  userAddress: "",
  zip: "",
  branchCode: "",
  branchInfo: "",
  branchLocation: "",
  cust_No: "",
  sqldbid: "",
  customer: "",
  franchise_slug: "",
  ndcInfo: "",
  addressInfo: "",
  billingEmail: "",
  franchiseData: {},
  serviceData: {},
  paymentInfo: {
    paymentServiceStatus: {},
    franchiseDiscounts: [],
    serviceYear: null,
    remainingMonths: null,
  },
  order: {},
  packages: {},
  metadata: {
    userContext: {
      lastRefresh: {
        branchInfo: null,
        franchiseInfo: null,
        scopeInfo: null,
        addressInfo: null,
        paymentInfo: null,
        cookies: null,
        orderInfo: null,
        serviceInfo: null,
        customerInfo: null,
        conditionalInfo: null,
      },
    },
    landingUrl: {
      fullPath: "",
      on: null,
    },
    referrerUrl: {
      fullPath: "",
      on: null,
    },
  },
};

const persistedState = (process.client && localStorage.getItem('persistedState')) 
  ? JSON.parse(localStorage.getItem('persistedState')) 
  : _.cloneDeep(emptyPersistedState);


const store = createStore({
  state() {
    return {
      persistedState: persistedState,
      showLoading: false,
      myAccountLayout: "",
      addCouponCode: "",
    };
  },

  getters: {
    getBranchCode(state) {
      return state.persistedState.branchCode;
    },
  },
  actions: {
    switchPersistanceState({ commit }, payload) {
      commit("SET_STATE", payload);
    },
    clearPersistanceState({ commit }) {
      commit("CLEAR_STATE");
    },
    updateMetadataUserContextLastRefresh({ commit, state }, payload) {
      let metaDataUpdate = _.cloneDeep(state.persistedState.metadata);

      // Handle initialization
      if (!metaDataUpdate) {
        metaDataUpdate = {};
      }
      if (!metaDataUpdate.userContext) {
        metaDataUpdate.userContext = {};
      }
      if (!metaDataUpdate.userContext.lastRefresh) {
        metaDataUpdate.userContext.lastRefresh = {};
      }

      // Update lastRefresh state
      metaDataUpdate.userContext.lastRefresh = {
        ...metaDataUpdate.userContext.lastRefresh,
        ...payload,
      };

      commit("SET_METADATA", metaDataUpdate);
    },
    updateMetadataLandingUrl({ commit, state }, fullPath) {
      let metaDataUpdate = _.cloneDeep(state.persistedState.metadata);

      // Handle initialization
      if (!metaDataUpdate) {
        metaDataUpdate = {};
      }

      // Update landingUrl
      metaDataUpdate.landingUrl = {
        fullPath: fullPath,
        on: Date.now(),
      };

      commit("SET_METADATA", metaDataUpdate);
    },
    updateMetadataReferrerUrl({ commit, state }, fullPath) {
      let metaDataUpdate = _.cloneDeep(state.persistedState.metadata);

      // Handle initialization
      if (!metaDataUpdate) {
        metaDataUpdate = {};
      }

      // Update referrerUrl
      metaDataUpdate.referrerUrl = {
        fullPath: fullPath,
        on: Date.now(),
      };

      commit("SET_METADATA", metaDataUpdate);
    },
    updatePaymentInfoPaymentServiceStatus({ commit, state }, payload) {
      let paymentInfoUpdate = _.cloneDeep(state.persistedState.paymentInfo);

      // Handle initialization
      if (!paymentInfoUpdate) {
        paymentInfoUpdate = {};
      }
      if (!paymentInfoUpdate.franchiseDiscounts) {
        paymentInfoUpdate.franchiseDiscounts = [];
      }

      paymentInfoUpdate.paymentServiceStatus = payload;

      commit("SET_PAYMENT_INFO", paymentInfoUpdate);
    },
    updatePaymentInfoFranchiseDiscounts({ commit, state }, payload) {
      let paymentInfoUpdate = _.cloneDeep(state.persistedState.paymentInfo);

      // Handle initialization
      if (!paymentInfoUpdate) {
        paymentInfoUpdate = {};
      }
      if (!paymentInfoUpdate.paymentServiceStatus) {
        paymentInfoUpdate.paymentServiceStatus = {};
      }

      paymentInfoUpdate.franchiseDiscounts = payload;

      commit("SET_PAYMENT_INFO", paymentInfoUpdate);
    },
    updatePaymentInfoServiceYear({ commit, state }, payload) {
      let paymentInfoUpdate = _.cloneDeep(state.persistedState.paymentInfo);

      // Handle initialization
      if (!paymentInfoUpdate) {
        paymentInfoUpdate = {};
      }

      paymentInfoUpdate.serviceYear = payload;

      commit("SET_PAYMENT_INFO", paymentInfoUpdate);
    },
    updatePaymentInfoRemainingMonths({ commit, state }, payload) {
      let paymentInfoUpdate = _.cloneDeep(state.persistedState.paymentInfo);

      // Handle initialization
      if (!paymentInfoUpdate) {
        paymentInfoUpdate = {};
      }

      paymentInfoUpdate.remainingMonths = payload;

      commit("SET_PAYMENT_INFO", paymentInfoUpdate);
    },
    updatePremiumPackageInfo({ commit, state }, payload) {
      let packagesInfoUpdate = _.cloneDeep(state.persistedState.packages);

      // Handle initialization
      if (!packagesInfoUpdate) {
        packagesInfoUpdate = {};
      }

      packagesInfoUpdate.premiumPackageInfo = payload;

      commit("SET_PACKAGES", packagesInfoUpdate);
    },
    updateIsOrderPremiumPackage({ commit, state }, payload) {
      let packagesInfoUpdate = _.cloneDeep(state.persistedState.packages);

      // Handle initialization
      if (!packagesInfoUpdate) {
        packagesInfoUpdate = {};
      }

      packagesInfoUpdate.isOrderPremiumPackage = payload;

      commit("SET_PACKAGES", packagesInfoUpdate);
    },
    updatePackageId({ commit, state }, payload) {
      commit("SET_PACKAGE_ID", payload);
    },
  },

  mutations: {
    SET_STATE(state, payload) {
      state.persistedState = {
        ..._.cloneDeep(emptyPersistedState),
        metadata: {
          landingUrl: state.persistedState?.metadata?.landingUrl,
          referrerUrl: state.persistedState?.metadata?.referrerUrl,
        },
        ...payload,
      };
    },
    CLEAR_STATE(state) {
      state.persistedState = _.cloneDeep(emptyPersistedState);
    },
    SET_ZIP(state, zip) {
      state.persistedState.zip = zip;
    },
    SET_BRANCHCODE(state, branchCode) {
      state.persistedState.branchCode = branchCode;
    },
    SET_BRANCH_INFO(state, branchInfo) {
      state.persistedState.branchInfo = branchInfo;
    },
    SET_FRANCHISE_DATA(state, franchiseData) {
      state.persistedState.franchiseData = franchiseData;
    },
    SET_SERVICE_DATA(state, serviceData) {
      state.persistedState.serviceData = serviceData;
    },
    SET_NDC_INFO(state, ndcInfo) {
      state.persistedState.ndcInfo = ndcInfo;
    },
    SET_BRANCH_PHONE(state, branchPhone) {
      state.persistedState.branchPhone = branchPhone;
    },
    SET_BRANCH_MARCHEXPHONE(state, branchMarchExPhone) {
      state.persistedState.branchMarchExPhone = branchMarchExPhone;
    },
    SET_BRANCH_LOCATION(state, location) {
      state.persistedState.branchLocation = location;
    },
    SET_SCOPE(state, scope) {
      state.persistedState.scope = scope;
    },
    SET_USER_ADDRESS(state, address) {
      state.persistedState.userAddress = address;
    },
    SET_ORDER_INFORMATION(state, order) {
      state.persistedState.order = order;
    },
    SET_SCOPE_ORDERID(state, orderId) {
      state.persistedState.scope.orderId = orderId;
    },
    SET_PACKAGES(state, packages) {
      state.persistedState.packages = packages;
    },
    SET_SHOW_LOADER(state, payload) {
      state.showLoading = payload;
    },
    SET_MYACCOUNT_LAYOUT(state, payload) {
      state.myAccountLayout = payload;
    },
    SET_MYACCOUNT_STEP(state, payload) {
      state.myAccountStep = payload;
    },
    SET_CUSTOMER_NUMBER(state, payload) {
      state.persistedState.cust_No = payload;
    },
    SET_SQLDBID(state, payload) {
      state.persistedState.sqldbid = payload;
    },
    SET_CUSTOMER(state, payload) {
      state.persistedState.customer = payload;
    },
    SET_FRANCHISE_SLUG(state, payload) {
      state.persistedState.franchise_slug = payload;
    },
    SET_CURRENT_PLAN(state, current_plan) {
      state.persistedState.current_plan = current_plan;
    },
    SET_ADDRESS_INFO(state, payload) {
      state.persistedState.addressInfo = payload;
    },
    SET_BILLING_EMAIL(state, payload) {
      state.persistedState.billingEmail = payload;
    },
    SET_ADD_COUPON(state, payload) {
      state.addCouponCode = payload;
    },
    SET_PAYMENT_INFO(state, payload) {
      state.persistedState.paymentInfo = payload;
    },
    SET_METADATA(state, payload) {
      state.persistedState.metadata = payload;
    },
    SET_ADDRESSLOOKUP(state, payload) {
      state.persistedState.addressLookup = payload
    },
    SET_EMAILCAPTUREFLAG(state, payload) {
      state.persistedState.emailCapture = payload
    },
    SET_PACKAGE_ID(state, payload) {
      state.persistedState.packageId = payload;
    },
  }
});

export default store;
